import React, { useState } from "react";
import { Link } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../config/firebase";
import styles from "./SharedSpendWaitlist.module.css";

const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={styles.logoIcon}>
    <path d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={styles.successIcon}>
    <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const features = [
  {
    icon: "👥",
    title: "Shared Budgets",
    description: "Create budgets with your household or friends and track spending together.",
  },
  {
    icon: "⚡",
    title: "Real-Time Sync",
    description: "Every member sees expenses the moment they're logged — no lag, no stale data.",
  },
  {
    icon: "🔔",
    title: "Spend Alerts",
    description: "Get notified before your group hits its limit so you can adjust in time.",
  },
  {
    icon: "📂",
    title: "Categories",
    description: "Organize spending across Food, Transport, Bills, Entertainment, and more.",
  },
];

type FormState = "idle" | "loading" | "success" | "error";

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const SharedSpendWaitlist: React.FC = () => {
  const [email, setEmail] = useState("");
  const [formState, setFormState] = useState<FormState>("idle");
  const [emailError, setEmailError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    setEmailError("");
    setFormState("loading");

    try {
      await addDoc(collection(db, "sharedspend-waitlist"), {
        email: trimmed,
        submittedAt: serverTimestamp(),
      });
      setFormState("success");
    } catch {
      setFormState("error");
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.backgroundGlow} />

      <nav className={styles.nav}>
        <div className={styles.navContainer}>
          <Link to="/shared-spend" className={styles.logo}>
            <LogoIcon />
            <span>Shared Spend</span>
          </Link>
        </div>
      </nav>

      <main id="main-content" className={styles.main} role="main" tabIndex={-1}>
        <div className={styles.container}>
          <div className={styles.badge}>Coming Soon to iOS</div>

          <h1 className={styles.headline}>
            Budget together,<br />without the stress.
          </h1>
          <p className={styles.subheadline}>
            Shared Spend lets you create real-time shared budgets with your household, partner, or roommates — and actually stay on track.
          </p>

          {formState === "success" ? (
            <div className={styles.successCard}>
              <CheckCircleIcon />
              <h2 className={styles.successTitle}>You're on the list!</h2>
              <p className={styles.successText}>
                We'll send you one email when Shared Spend launches on iOS. No spam, ever.
              </p>
              <Link to="/shared-spend" className={styles.backLink}>
                Back to Shared Spend
              </Link>
            </div>
          ) : (
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <div className={styles.inputRow}>
                <div className={styles.inputWrapper}>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError("");
                    }}
                    className={`${styles.input} ${emailError ? styles.inputError : ""}`}
                    aria-label="Email address"
                    aria-describedby={emailError ? "email-error" : undefined}
                    disabled={formState === "loading"}
                    autoComplete="email"
                  />
                  {emailError && (
                    <p id="email-error" className={styles.errorText} role="alert">
                      {emailError}
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={formState === "loading"}
                >
                  {formState === "loading" ? (
                    <span className={styles.spinner} aria-hidden="true" />
                  ) : (
                    "Notify Me"
                  )}
                </button>
              </div>

              <p className={styles.consent}>
                By signing up you agree to receive one launch notification.
                No spam. Unsubscribe by emailing{" "}
                <a href="mailto:dixonappdevelopment@gmail.com" className={styles.consentLink}>
                  dixonappdevelopment@gmail.com
                </a>
                .
              </p>

              {formState === "error" && (
                <p className={styles.errorText} role="alert">
                  Something went wrong. Please try again or email us directly.
                </p>
              )}
            </form>
          )}

          <div className={styles.features}>
            {features.map((f) => (
              <div key={f.title} className={styles.featureCard}>
                <div className={styles.featureEmoji}>{f.icon}</div>
                <h3 className={styles.featureTitle}>{f.title}</h3>
                <p className={styles.featureDescription}>{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      <footer className={styles.footer}>
        <p>
          &copy; {new Date().getFullYear()} Dixon App Development &middot;{" "}
          <Link to="/shared-spend/privacy" className={styles.footerLink}>
            Privacy Policy
          </Link>
        </p>
      </footer>
    </div>
  );
};
